import { useState, useEffect, useCallback } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { useAuth } from "../App";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { 
  Plus, Users, Phone, Globe, DollarSign,
  Pencil, Trash2, Filter, X, GripVertical, FileText, Calendar, Clock
} from "lucide-react";

const ORIGENS = ["Instagram", "YouTube", "Tráfego Pago", "Indicação"];
const MOEDAS = ["R$", "€"];
const STATUS_CONFIG = {
  novo: { label: "Novo", color: "bg-blue-500", icon: "🆕" },
  negociando: { label: "Negociando", color: "bg-amber-500", icon: "🤝" },
  contrato_feito: { label: "Contrato Feito", color: "bg-purple-500", icon: "📄" },
  fechado: { label: "Fechado", color: "bg-emerald-500", icon: "✅" },
};

const clientSchema = z.object({
  nome: z.string().min(2, "Mínimo 2 caracteres"),
  telefone: z.string().min(8, "Telefone inválido"),
  servidor: z.string().min(1, "Servidor obrigatório"),
  origem: z.string().min(1, "Origem obrigatória"),
  valor: z.coerce.number().min(0).optional().default(0),
  moeda: z.string().optional().default("R$"),
  status: z.string().min(1, "Status obrigatório"),
  data_contato: z.string().optional().default(""),
  descricao: z.string().optional().default(""),
  data_inicio: z.string().optional().default(""),
  data_termino: z.string().optional().default(""),
});

// Format datetime for display
const formatDateTime = (dateStr) => {
  if (!dateStr) return "";
  try {
    const date = new Date(dateStr);
    return date.toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  } catch {
    return dateStr;
  }
};

// Client Card Component
const ClientCard = ({ client, index, onEdit, onDelete }) => {
  return (
    <Draggable draggableId={client.id} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          className={`p-4 bg-card rounded-lg border shadow-sm transition-all cursor-grab active:cursor-grabbing ${
            snapshot.isDragging ? "shadow-xl ring-2 ring-primary/50 rotate-1 scale-105" : "hover:shadow-md"
          }`}
          style={{
            ...provided.draggableProps.style,
          }}
        >
          <div className="flex items-start justify-between gap-2">
            <div className="text-muted-foreground hover:text-foreground">
              <GripVertical className="w-4 h-4" />
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="font-semibold truncate">{client.nome}</h4>
              <div className="mt-2 space-y-1.5">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Phone className="w-3.5 h-3.5 flex-shrink-0" />
                  <span className="truncate">{client.telefone}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Globe className="w-3.5 h-3.5 flex-shrink-0" />
                  <span className="truncate">{client.origem}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <DollarSign className="w-3.5 h-3.5 flex-shrink-0" />
                  <span className="number-display">
                    {client.moeda} {client.valor.toLocaleString("pt-BR")}
                  </span>
                </div>
                
                {/* Descrição */}
                {client.descricao && (
                  <div className="flex items-start gap-2 text-sm text-muted-foreground">
                    <FileText className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                    <span>{client.descricao}</span>
                  </div>
                )}
                
                {/* Data/Hora Início e Término em destaque */}
                {(client.data_inicio || client.data_termino) && (
                  <div className="mt-2 p-2 bg-primary/5 rounded border-l-2 border-primary space-y-1">
                    {client.data_inicio && (
                      <div className="flex items-center gap-2 text-sm">
                        <Calendar className="w-3.5 h-3.5 flex-shrink-0 text-emerald-500" />
                        <span className="text-emerald-600 font-medium">Início:</span>
                        <span className="text-foreground">{formatDateTime(client.data_inicio)}</span>
                      </div>
                    )}
                    {client.data_termino && (
                      <div className="flex items-center gap-2 text-sm">
                        <Clock className="w-3.5 h-3.5 flex-shrink-0 text-red-500" />
                        <span className="text-red-600 font-medium">Término:</span>
                        <span className="text-foreground">{formatDateTime(client.data_termino)}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="flex flex-col gap-1">
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={(e) => { e.stopPropagation(); onEdit(client); }}
                data-testid={`edit-client-${client.id}`}
              >
                <Pencil className="w-3.5 h-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-destructive hover:text-destructive"
                onClick={(e) => { e.stopPropagation(); onDelete(client.id); }}
                data-testid={`delete-client-${client.id}`}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </Draggable>
  );
};

// Kanban Column Component
const KanbanColumn = ({ status, clients, onEdit, onDelete }) => {
  const config = STATUS_CONFIG[status];
  
  return (
    <div className="flex flex-col gap-4 min-w-[300px] w-[300px] bg-muted/50 p-4 rounded-xl border border-border/50">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-lg">{config.icon}</span>
          <h3 className="font-semibold">{config.label}</h3>
        </div>
        <Badge variant="secondary" className="rounded-full">
          {clients.length}
        </Badge>
      </div>
      
      <Droppable droppableId={status}>
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            className={`flex-1 space-y-3 min-h-[300px] p-2 rounded-lg transition-colors ${
              snapshot.isDraggingOver ? "bg-primary/10 border-2 border-dashed border-primary/30" : ""
            }`}
          >
            {clients.map((client, index) => (
              <ClientCard
                key={client.id}
                client={client}
                index={index}
                onEdit={onEdit}
                onDelete={onDelete}
              />
            ))}
            {provided.placeholder}
            {clients.length === 0 && !snapshot.isDraggingOver && (
              <div className="flex items-center justify-center h-24 text-sm text-muted-foreground border-2 border-dashed border-border rounded-lg">
                Arraste clientes aqui
              </div>
            )}
          </div>
        )}
      </Droppable>
    </div>
  );
};

export default function CRMPage() {
  const { authAxios } = useAuth();
  const [clients, setClients] = useState([]);
  const [stats, setStats] = useState({ total: 0, por_status: {} });
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Filters
  const [filterStatus, setFilterStatus] = useState("");
  const [filterOrigem, setFilterOrigem] = useState("");
  const [filterServidor, setFilterServidor] = useState("");

  const form = useForm({
    resolver: zodResolver(clientSchema),
    defaultValues: {
      nome: "",
      telefone: "",
      servidor: "",
      origem: "",
      valor: 0,
      moeda: "R$",
      status: "novo",
      data_contato: "",
    },
  });

  const fetchClients = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterStatus && filterStatus !== "all") params.append("status", filterStatus);
      if (filterOrigem && filterOrigem !== "all") params.append("origem", filterOrigem);
      if (filterServidor && filterServidor !== "all") params.append("servidor", filterServidor);
      
      const res = await authAxios.get(`/clients?${params.toString()}`);
      setClients(res.data);
    } catch (err) {
      toast.error("Erro ao carregar clientes");
    } finally {
      setLoading(false);
    }
  }, [authAxios, filterStatus, filterOrigem, filterServidor]);

  const fetchStats = async () => {
    try {
      const res = await authAxios.get("/clients/stats");
      setStats(res.data);
    } catch (err) {
      console.error("Error fetching stats:", err);
    }
  };

  useEffect(() => {
    fetchClients();
    fetchStats();
  }, [fetchClients]);

  const handleOpenDialog = (client = null) => {
    if (client) {
      setEditingClient(client);
      form.reset({
        nome: client.nome,
        telefone: client.telefone,
        servidor: client.servidor,
        origem: client.origem,
        valor: client.valor || 0,
        moeda: client.moeda || "R$",
        status: client.status,
        data_contato: client.data_contato || "",
        descricao: client.descricao || "",
        data_inicio: client.data_inicio || "",
        data_termino: client.data_termino || "",
      });
    } else {
      setEditingClient(null);
      form.reset({
        nome: "",
        telefone: "",
        servidor: "",
        origem: "",
        valor: 0,
        moeda: "R$",
        status: "novo",
        data_contato: "",
        descricao: "",
        data_inicio: "",
        data_termino: "",
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (data) => {
    setSubmitting(true);
    try {
      if (editingClient) {
        await authAxios.put(`/clients/${editingClient.id}`, data);
        toast.success("Cliente atualizado!");
      } else {
        await authAxios.post("/clients", data);
        toast.success("Cliente criado!");
      }
      setDialogOpen(false);
      setEditingClient(null);
      fetchClients();
      fetchStats();
    } catch (err) {
      toast.error("Erro ao salvar cliente");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Tem certeza que deseja excluir este cliente?")) return;
    try {
      await authAxios.delete(`/clients/${id}`);
      toast.success("Cliente excluído!");
      fetchClients();
      fetchStats();
    } catch (err) {
      toast.error("Erro ao excluir cliente");
    }
  };

  const handleDragEnd = async (result) => {
    const { destination, source, draggableId } = result;
    
    if (!destination) return;
    if (destination.droppableId === source.droppableId && destination.index === source.index) return;
    
    const newStatus = destination.droppableId;
    
    // Update local state immediately
    setClients(prev => prev.map(c => 
      c.id === draggableId ? { ...c, status: newStatus } : c
    ));
    
    // Update on server
    try {
      await authAxios.put(`/clients/${draggableId}`, { status: newStatus });
      fetchStats();
      toast.success(`Cliente movido para ${STATUS_CONFIG[newStatus].label}`);
    } catch (err) {
      fetchClients();
      toast.error("Erro ao atualizar status");
    }
  };

  const clearFilters = () => {
    setFilterStatus("");
    setFilterOrigem("");
    setFilterServidor("");
  };

  const hasFilters = filterStatus || filterOrigem || filterServidor;
  const servers = [...new Set(clients.map(c => c.servidor).filter(Boolean))];

  // Group clients by status
  const clientsByStatus = {
    novo: clients.filter(c => c.status === "novo"),
    negociando: clients.filter(c => c.status === "negociando"),
    contrato_feito: clients.filter(c => c.status === "contrato_feito"),
    fechado: clients.filter(c => c.status === "fechado"),
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="crm-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold">CRM</h2>
          <p className="text-muted-foreground">Gestão de leads e clientes</p>
        </div>
        
        <Button onClick={() => handleOpenDialog()} data-testid="new-client-btn">
          <Plus className="w-4 h-4 mr-2" />
          Novo Cliente
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <Users className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Total de Leads</p>
                <p className="text-xl font-bold number-display">{stats.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        {Object.entries(STATUS_CONFIG).map(([key, config]) => (
          <Card key={key}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <span className="text-2xl">{config.icon}</span>
                <div>
                  <p className="text-xs text-muted-foreground">{config.label}</p>
                  <p className="text-xl font-bold number-display">{stats.por_status?.[key] || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm font-medium">Filtros:</span>
            </div>
            
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-40" data-testid="filter-status">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os Status</SelectItem>
                {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                  <SelectItem key={key} value={key}>{config.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={filterOrigem} onValueChange={setFilterOrigem}>
              <SelectTrigger className="w-40" data-testid="filter-origem">
                <SelectValue placeholder="Origem" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas Origens</SelectItem>
                {ORIGENS.map(origem => (
                  <SelectItem key={origem} value={origem}>{origem}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={filterServidor} onValueChange={setFilterServidor}>
              <SelectTrigger className="w-40" data-testid="filter-servidor">
                <SelectValue placeholder="Servidor" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos Servidores</SelectItem>
                {servers.map(servidor => (
                  <SelectItem key={servidor} value={servidor}>{servidor}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {hasFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                <X className="w-4 h-4 mr-1" />
                Limpar
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Kanban Board */}
      <DragDropContext onDragEnd={handleDragEnd}>
        <div className="flex gap-6 overflow-x-auto pb-4" style={{ minHeight: "500px" }}>
          {Object.keys(STATUS_CONFIG).map(status => (
            <KanbanColumn
              key={status}
              status={status}
              clients={clientsByStatus[status]}
              onEdit={handleOpenDialog}
              onDelete={handleDelete}
            />
          ))}
        </div>
      </DragDropContext>

      {/* Client Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingClient ? "Editar Cliente" : "Novo Cliente"}</DialogTitle>
            <DialogDescription>
              Preencha os dados do cliente
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="nome">Nome</Label>
                <Input
                  id="nome"
                  placeholder="Nome do cliente"
                  data-testid="input-client-nome"
                  {...form.register("nome")}
                />
                {form.formState.errors.nome && (
                  <p className="text-sm text-destructive">{form.formState.errors.nome.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="telefone">Telefone</Label>
                <Input
                  id="telefone"
                  placeholder="(00) 00000-0000"
                  data-testid="input-client-telefone"
                  {...form.register("telefone")}
                />
                {form.formState.errors.telefone && (
                  <p className="text-sm text-destructive">{form.formState.errors.telefone.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="servidor">Servidor</Label>
                <Input
                  id="servidor"
                  placeholder="Ex: AWS, Google Cloud"
                  data-testid="input-client-servidor"
                  {...form.register("servidor")}
                />
                {form.formState.errors.servidor && (
                  <p className="text-sm text-destructive">{form.formState.errors.servidor.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label>Origem</Label>
                <Select
                  value={form.watch("origem")}
                  onValueChange={(v) => form.setValue("origem", v)}
                >
                  <SelectTrigger data-testid="select-client-origem">
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    {ORIGENS.map(origem => (
                      <SelectItem key={origem} value={origem}>{origem}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {form.formState.errors.origem && (
                  <p className="text-sm text-destructive">{form.formState.errors.origem.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label>Status</Label>
                <Select
                  value={form.watch("status")}
                  onValueChange={(v) => form.setValue("status", v)}
                >
                  <SelectTrigger data-testid="select-client-status">
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                      <SelectItem key={key} value={key}>
                        {config.icon} {config.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {form.formState.errors.status && (
                  <p className="text-sm text-destructive">{form.formState.errors.status.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="valor">Valor <span className="text-muted-foreground text-xs">(opcional)</span></Label>
                <Input
                  id="valor"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0,00"
                  data-testid="input-client-valor"
                  {...form.register("valor")}
                />
              </div>

              <div className="space-y-2">
                <Label>Moeda <span className="text-muted-foreground text-xs">(opcional)</span></Label>
                <Select
                  value={form.watch("moeda")}
                  onValueChange={(v) => form.setValue("moeda", v)}
                >
                  <SelectTrigger data-testid="select-client-moeda">
                    <SelectValue placeholder="Moeda" />
                  </SelectTrigger>
                  <SelectContent>
                    {MOEDAS.map(moeda => (
                      <SelectItem key={moeda} value={moeda}>{moeda}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="descricao">Descrição <span className="text-muted-foreground text-xs">(opcional)</span></Label>
                <Textarea
                  id="descricao"
                  placeholder="Observações sobre o cliente..."
                  className="min-h-[80px]"
                  data-testid="input-client-descricao"
                  {...form.register("descricao")}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="data_inicio">Data/Hora Início do Teste</Label>
                <Input
                  id="data_inicio"
                  type="datetime-local"
                  data-testid="input-client-data-inicio"
                  {...form.register("data_inicio")}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="data_termino">Data/Hora Término do Teste</Label>
                <Input
                  id="data_termino"
                  type="datetime-local"
                  data-testid="input-client-data-termino"
                  {...form.register("data_termino")}
                />
              </div>

              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="data_contato">Data de Contato <span className="text-muted-foreground text-xs">(opcional)</span></Label>
                <Input
                  id="data_contato"
                  type="date"
                  data-testid="input-client-data"
                  {...form.register("data_contato")}
                />
              </div>
            </div>

            <DialogFooter className="gap-2 sm:gap-0">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setDialogOpen(false)}
              >
                Cancelar
              </Button>
              <Button 
                type="submit" 
                disabled={submitting}
                data-testid="submit-client-btn"
              >
                {submitting ? "Salvando..." : editingClient ? "Atualizar" : "Criar Cliente"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
